// =============================================================================
// PPIProcessos — CRUD de processos (edições) e suas vagas (cargos)
// =============================================================================
// Três telas: lista → novo (cria processo + vagas iniciais num POST) → detalhe
// (edita o cabeçalho, gere vagas uma a uma, publica/muda status, mostra o link
// público de inscrição e permite apagar um processo de teste).

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Plus, ArrowLeft, ArrowRight, Trash2, Copy, Check, Users, UserPlus, Briefcase, Send, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
import { CORES, BadgeStatus, Botao, SeletorMulti, estilos } from './ppiUI';
import PPIEnvios from './PPIEnvios';
import PPIExcecoes from './PPIExcecoes';

// Header autenticado (token do portal em localStorage['authToken']).
const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('authToken')}`,
});

// Opções dos seletores do recorte (lojas / centros de lucro / cargos compilados).
//
// INTERATIVO (2026-07-16): recarrega a cada mudança no recorte — o backend devolve,
// em cada dimensão, só o que existe dentro do que já foi escolhido nas OUTRAS duas.
// Ex.: com "01 | Diretoria" marcado, o seletor de Cargos oferece o único cargo que
// existe lá, não os 42 da rede.
//
// Sem opções (erro de rede), os seletores ficam vazios = recorte "todos" — por isso
// o catch é silencioso: a tela não quebra, e a prévia de destinatários (que vem do
// servidor) continua sendo a verdade sobre quem recebe.
// ⚠️ NADA de juntar os valores num separador ("a|b", "a,b"). Os nomes dos centros
// de lucro TÊM pipe no meio ("03 | Apoio Administrativo") e o cargo pode ter
// qualquer pontuação. Qualquer delimitador que a gente escolha, um dia aparece
// dentro do dado e o valor é partido em dois — foi exatamente o que aconteceu na
// 1ª versão (2026-07-16): o recorte chegava ao servidor como "03" + "Apoio
// Administrativo", não casava com ninguém, e a tela mostrava zero cargos.
// A query vai com CHAVE REPETIDA (?centrosLucro=A&centrosLucro=B) — o Express
// entrega um array e não existe delimitador para colidir.
function useOpcoesFiltro(filtros) {
    const [opcoes, setOpcoes] = useState({ lojas: [], centrosLucro: [], cargos: [] });

    // Dependência do efeito: o objeto `filtros` é novo a cada render e o fetch
    // entraria em laço. O JSON é estável e, ao contrário de um join, não pode ser
    // confundido com o conteúdo dos valores.
    const chave = JSON.stringify([filtros.lojas || [], filtros.centrosLucro || [], filtros.cargos || []]);

    useEffect(() => {
        let cancelado = false;
        (async () => {
            try {
                const [lojas, centrosLucro, cargos] = JSON.parse(chave);
                const qs = new URLSearchParams();
                // append (não set): uma entrada por valor, já escapada pelo próprio
                // URLSearchParams — pipe, espaço e acento inclusive.
                lojas.forEach((v) => qs.append('lojas', v));
                centrosLucro.forEach((v) => qs.append('centrosLucro', v));
                cargos.forEach((v) => qs.append('cargos', v));
                const r = await fetch(`/api/treinamento/ppi/filtros/opcoes?${qs}`, { headers: authHeaders() });
                // A resposta pode chegar fora de ordem se o RH clicar rápido — a
                // flag descarta a que já não vale mais.
                if (r.ok && !cancelado) setOpcoes(await r.json());
            } catch (_) { /* silencioso — sem opções o recorte fica "todos" */ }
        })();
        return () => { cancelado = true; };
    }, [chave]);

    return opcoes;
}

const filtrosVazios = () => ({ lojas: [], centrosLucro: [], cargos: [] });

// O que o seletor mostra = as opções do recorte MAIS o que já está marcado.
// Sem a união, marcar o cargo X e depois escolher um centro sem X faria o X sumir
// da lista — mas ele CONTINUARIA valendo no filtro, invisível. Como o recorte
// decide quem recebe mensagem paga, um filtro que age escondido é inaceitável:
// melhor mostrar o X ali, marcado, para o RH ver e desmarcar se quiser.
const comSelecionados = (opcoes, selecionados) =>
    [...new Set([...(opcoes || []), ...(selecionados || [])])].sort((a, b) => a.localeCompare(b, 'pt-BR'));

// Bloco dos 3 seletores do recorte. "Nada selecionado = todos" é a regra do backend.
function PublicoAlvo({ opcoes, filtros, setFiltros }) {
    const set = (campo) => (valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
    return (
        <div>
            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '0 0 10px 0' }}>
                Deixe em branco para <strong>todos</strong>. O recorte define <strong>quem recebe o aviso</strong> e
                também <strong>quem pode se inscrever</strong>. Os três filtros se cruzam: escolher um centro de lucro
                já reduz as lojas e os cargos oferecidos.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
                <SeletorMulti label="Lojas" opcoes={comSelecionados(opcoes.lojas, filtros.lojas)} selecionados={filtros.lojas} onChange={set('lojas')} dica="Buscar loja…" />
                <SeletorMulti label="Centros de lucro" opcoes={comSelecionados(opcoes.centrosLucro, filtros.centrosLucro)} selecionados={filtros.centrosLucro} onChange={set('centrosLucro')} dica="Buscar centro…" />
                <SeletorMulti label="Cargos" opcoes={comSelecionados(opcoes.cargos, filtros.cargos)} selecionados={filtros.cargos} onChange={set('cargos')} dica="Buscar cargo…" />
            </div>
        </div>
    );
}

// ─── Cargo e Local da vaga — listas suspensas (2026-07-16) ───────────────────
// Antes eram texto livre, e cada pessoa digitava de um jeito ("Treinador",
// "treinador", "Trein."). Como o cargo da vaga é o que o candidato lê no link
// público e o que amarra a prova, a grafia não pode variar.
//
// A lista é uma CONSTANTE aqui, de propósito: são os destinos das trilhas de
// promoção da dtbPpiCargo, que mudam de ano em ano, não de semana em semana. O
// backend segue validando só "não vazio" — duplicar a lista lá criaria uma segunda
// fonte da verdade e barraria as vagas já gravadas.
const CARGOS_VAGA = ['Anfitrião', 'Treinador', 'Coordenador', 'Gerente de Plantão', 'Gerente de Treinamento'];

// Locais conhecidos. O "Outro…" libera o campo de texto — a avaliação quase sempre
// é no escritório, mas travar a lista impediria uma exceção legítima (uma loja, um
// centro de treinamento novo) de ser cadastrada sem deploy.
const LOCAL_ESCRITORIO = 'Escritório - Av. Princesa Isabel, 287 - Centro, Vitória - ES';
const LOCAIS_VAGA = [LOCAL_ESCRITORIO];
const OUTRO = '__outro__';

// Select do local + input que aparece só no "Outro…". O estado é o valor final
// (string do local), não a escolha do select — quem consome a vaga não precisa
// saber que existe um "Outro".
function CampoLocal({ valor, onChange, style }) {
    // Um valor que não está na lista (vaga antiga, ou o RH já digitou) mantém o
    // campo livre aberto — senão o texto sumiria da tela ao editar a vaga.
    const [livre, setLivre] = useState(() => Boolean(valor) && !LOCAIS_VAGA.includes(valor));

    const escolher = (e) => {
        const v = e.target.value;
        if (v === OUTRO) { setLivre(true); onChange(''); return; }
        setLivre(false); onChange(v);
    };

    return (
        <div style={{ display: 'flex', gap: '8px', flex: 2, minWidth: '220px', ...style }}>
            <select style={{ ...estilos.input, flex: livre ? 0 : 1, width: livre ? '96px' : undefined }}
                value={livre ? OUTRO : valor} onChange={escolher} title="Local da avaliação">
                <option value="">Local da avaliação…</option>
                {LOCAIS_VAGA.map((l) => <option key={l} value={l}>{l}</option>)}
                <option value={OUTRO}>Outro…</option>
            </select>
            {livre && (
                <input style={{ ...estilos.input, flex: 1 }} value={valor}
                    onChange={(e) => onChange(e.target.value)} placeholder="Digite o local" autoFocus />
            )}
        </div>
    );
}

// ─── A ESTEIRA do processo (2026-07-14) ──────────────────────────────────────
// A ordem é a mesma do backend (ETAPAS em server-ppi.js) e do ENUM no banco — é ela
// que define o que o botão "Avançar etapa" oferece. O avanço é MANUAL: o processo
// não pula de etapa sozinho porque um candidato atrasado respondeu.
const ETAPAS = [
    { chave: 'Rascunho', rotulo: 'Rascunho', tom: 'rascunho' },
    { chave: 'Inscricoes', rotulo: 'Inscrições', tom: 'ativo' },
    { chave: 'Triagem', rotulo: 'Triagem', tom: 'emProcesso' },
    { chave: 'AplicacaoProva', rotulo: 'Aplicação da Prova', tom: 'inscrito' },
    { chave: 'AvaliacaoChao', rotulo: 'LIVE', tom: 'alerta' },
    { chave: 'Finalizado', rotulo: 'Finalizado', tom: 'neutro' },
];
const etapaDe = (chave) => ETAPAS.find((e) => e.chave === chave) || ETAPAS[0];
const proximaEtapa = (chave) => ETAPAS[ETAPAS.findIndex((e) => e.chave === chave) + 1] || null;

// Data ISO (YYYY-MM-DD) de N meses atrás / de hoje — para o default da apuração.
const isoHoje = () => new Date().toISOString().slice(0, 10);
const isoMesesAtras = (n) => {
    const d = new Date();
    d.setMonth(d.getMonth() - n);
    return d.toISOString().slice(0, 10);
};

// DATETIME do MySQL → "14/07/2026 09:12". Vazio vira null (o chamador decide o "—").
// ⚠️ Aqui é seguro usar new Date(): é DATETIME (tem hora), não data-só. A armadilha
// do fuso ("um dia antes") só existe com 'AAAA-MM-DD' puro, que o JS lê como UTC.
const dataHora = (v) => (v ? new Date(v).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : null);
const soData = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : null);

// 'AAAA-MM-DD' → 'DD/MM/AAAA' SEM passar por new Date(). É a armadilha citada acima:
// data-só é lida como meia-noite UTC e, no nosso fuso (-3), sairia um dia antes.
const dataIsoBR = (s) => {
    const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

// Quantos dias o processo esteve (ou está) no ar. Derivado — não é coluna no banco:
// guardar seria duplicar a verdade e abrir espaço para os dois valores divergirem.
function diasAtivo(publicacao, finalizacao) {
    if (!publicacao) return null;
    const ini = new Date(publicacao);
    const fim = finalizacao ? new Date(finalizacao) : new Date();
    return Math.max(0, Math.round((fim - ini) / 86400000));
}

const linhaVagaVazia = () => ({ cargo: '', dataAvaliacao: '', horaAvaliacao: '', localAvaliacao: '' });

function PPIProcessos() {
    const [view, setView] = useState('lista'); // lista | novo | detalhe
    const [processos, setProcessos] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [idAberto, setIdAberto] = useState(null);

    const carregarLista = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const resp = await fetch('/api/treinamento/ppi/processos', { headers: authHeaders() });
            if (!resp.ok) throw new Error('Erro ao carregar processos.');
            setProcessos(await resp.json());
        } catch (e) { setErro(e.message); }
        finally { setCarregando(false); }
    }, []);

    useEffect(() => { carregarLista(); }, [carregarLista]);

    const abrirDetalhe = (id) => { setIdAberto(id); setView('detalhe'); };
    const voltarLista = () => { setView('lista'); setIdAberto(null); carregarLista(); };

    if (view === 'novo') return <NovoProcesso onVoltar={voltarLista} />;
    if (view === 'detalhe') return <DetalheProcesso id={idAberto} onVoltar={voltarLista} />;

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <h3 style={estilos.tituloSecao}>Processos</h3>
                <Botao icone={Plus} onClick={() => setView('novo')}>Novo processo</Botao>
            </div>

            {carregando && <p style={{ color: CORES.textoFraco }}>Carregando…</p>}
            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}
            {!carregando && !processos.length && (
                <p style={{ color: CORES.textoFraco }}>Nenhum processo cadastrado ainda.</p>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                {processos.map((p) => (
                    <div key={p.ID} onClick={() => abrirDetalhe(p.ID)}
                        style={{ backgroundColor: '#fff', border: `1px solid ${CORES.borda}`, borderRadius: '14px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 20px -6px rgba(0,0,0,0.12)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                            <div>
                                {/* O CÓDIGO vem antes do título: é por ele que o RH identifica o processo. */}
                                {p.CODIGO && <div style={estiloCodigo}>{p.CODIGO}</div>}
                                <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: '2px 0 0 0' }}>{p.NOME_PROCESSO}</h4>
                            </div>
                            <BadgeStatus tom={etapaDe(p.STATUS).tom}>{etapaDe(p.STATUS).rotulo}</BadgeStatus>
                        </div>
                        <div style={{ display: 'flex', gap: '18px', color: CORES.textoFraco, fontSize: '13px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Briefcase size={14} /> {p.QTD_VAGAS} vaga(s)</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}><Users size={14} /> {p.QTD_CANDIDATOS} inscrito(s)</span>
                        </div>
                        {/* Quem criou entra AQUI, na lista, e não só no detalhe: a pergunta
                            "de quem é esse processo?" é feita olhando o conjunto, antes de
                            abrir qualquer um. Nome só aparece quando existe — processo antigo
                            sem autor mostra a data sozinha, como sempre mostrou. */}
                        <div style={{ marginTop: '10px', fontSize: '12px', color: CORES.textoFraco }}>
                            Criado em {soData(p.DATA_CRIACAO) || '—'}
                            {p.NOME_USUARIO_CRIACAO && ` por ${p.NOME_USUARIO_CRIACAO}`}
                            {p.DATA_FINALIZACAO && ` · finalizado em ${soData(p.DATA_FINALIZACAO)}`}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ─── Novo processo (cabeçalho + vagas iniciais → 1 POST) ─────────────────────
function NovoProcesso({ onVoltar }) {
    const [form, setForm] = useState({
        nome: '', descricao: '', inscricaoAbre: isoHoje(), inscricaoFecha: '',
        periodoApuracaoInicio: isoMesesAtras(12), periodoApuracaoFim: isoHoje(),
    });
    const [vagas, setVagas] = useState([linhaVagaVazia()]);
    const [filtros, setFiltros] = useState(filtrosVazios());
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState('');
    const opcoes = useOpcoesFiltro(filtros);   // recarrega a cada mudança no recorte

    const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));
    const setVaga = (i, campo) => (e) => setVagas((vs) => vs.map((v, k) => k === i ? { ...v, [campo]: e.target.value } : v));
    const addVaga = () => setVagas((vs) => [...vs, linhaVagaVazia()]);
    const rmVaga = (i) => setVagas((vs) => vs.filter((_, k) => k !== i));

    const salvar = async () => {
        setErro('');
        if (!form.nome.trim()) { setErro('Informe o nome do processo.'); return; }
        // O fechamento é OBRIGATÓRIO desde 2026-08-04: é contra essa data que o
        // servidor mede os 6 meses no cargo. Barrar aqui evita o 400 do backend
        // chegar como erro genérico depois de a pessoa ter preenchido tudo.
        if (!form.inscricaoFecha) { setErro('Informe a data em que as inscrições fecham — é ela que define quem tem os 6 meses no cargo.'); return; }
        if (!vagas.some((v) => v.cargo.trim())) { setErro('Cadastre ao menos uma vaga (cargo).'); return; }
        setSalvando(true);
        try {
            const resp = await fetch('/api/treinamento/ppi/processos', {
                method: 'POST', headers: authHeaders(),
                body: JSON.stringify({
                    ...form,
                    vagas: vagas.filter((v) => v.cargo.trim()),
                    filtroLojas: filtros.lojas,
                    filtroCentrosLucro: filtros.centrosLucro,
                    filtroCargos: filtros.cargos,
                }),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Erro ao criar o processo.');
            onVoltar();
        } catch (e) { setErro(e.message); }
        finally { setSalvando(false); }
    };

    return (
        <div>
            <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
            <h3 style={{ ...estilos.tituloSecao, marginBottom: '20px' }}>Novo processo</h3>

            <div style={grid2}>
                <Campo label="Nome do processo *"><input style={estilos.input} value={form.nome} onChange={set('nome')} placeholder="Ex.: Promoção Interna Q1/2026" /></Campo>
                <Campo label="Descrição"><input style={estilos.input} value={form.descricao} onChange={set('descricao')} placeholder="Opcional" /></Campo>
                <Campo label="Inscrições abrem"><input type="date" style={estilos.input} value={form.inscricaoAbre} onChange={set('inscricaoAbre')} /></Campo>
                <Campo label="Inscrições fecham *"><input type="date" style={estilos.input} value={form.inscricaoFecha} onChange={set('inscricaoFecha')} /></Campo>
                <Campo label="Apuração de faltas/atestados — início"><input type="date" style={estilos.input} value={form.periodoApuracaoInicio} onChange={set('periodoApuracaoInicio')} /></Campo>
                <Campo label="Apuração de faltas/atestados — fim"><input type="date" style={estilos.input} value={form.periodoApuracaoFim} onChange={set('periodoApuracaoFim')} /></Campo>
            </div>
            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '4px 0 24px 0' }}>
                A janela de apuração vem preenchida com os últimos 12 meses — ajuste se precisar.
                <br />
                Só participa quem tiver <strong>6 meses no cargo atual</strong> até a data em que as inscrições fecham —
                por isso ela é obrigatória. Quem completa os 6 meses durante a janela já entra no aviso.
            </p>

            <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: '0 0 8px 0' }}>Público-alvo do processo</h4>
            <PublicoAlvo opcoes={opcoes} filtros={filtros} setFiltros={setFiltros} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '28px 0 12px 0' }}>
                <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>Vagas (cargos)</h4>
                <Botao variant="secundario" icone={Plus} onClick={addVaga}>Adicionar vaga</Botao>
            </div>

            {vagas.map((v, i) => (
                <div key={i} style={linhaVaga}>
                    <select style={{ ...estilos.input, flex: 2 }} value={v.cargo} onChange={setVaga(i, 'cargo')} title="Cargo da vaga">
                        <option value="">Cargo…</option>
                        {CARGOS_VAGA.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="date" style={{ ...estilos.input, flex: 1 }} value={v.dataAvaliacao} onChange={setVaga(i, 'dataAvaliacao')} title="Data da avaliação" />
                    <input type="time" style={{ ...estilos.input, width: '110px' }} value={v.horaAvaliacao} onChange={setVaga(i, 'horaAvaliacao')} title="Hora" />
                    <CampoLocal valor={v.localAvaliacao} onChange={(l) => setVagas((vs) => vs.map((x, k) => k === i ? { ...x, localAvaliacao: l } : x))} />
                    <button onClick={() => rmVaga(i)} style={btnIconePerigo} title="Remover"><Trash2 size={16} /></button>
                </div>
            ))}

            {erro && <p style={{ color: '#be123c', marginTop: '12px' }}>{erro}</p>}
            <div style={{ marginTop: '24px' }}>
                <Botao onClick={salvar} disabled={salvando}>{salvando ? 'Salvando…' : 'Criar processo'}</Botao>
            </div>
        </div>
    );
}

// ─── Detalhe / edição de um processo existente ───────────────────────────────
function DetalheProcesso({ id, onVoltar }) {
    const [processo, setProcesso] = useState(null);
    const [vagas, setVagas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [copiado, setCopiado] = useState(false);
    const [novaVaga, setNovaVaga] = useState(linhaVagaVazia());
    const [filtros, setFiltros] = useState(filtrosVazios());
    const [salvandoRecorte, setSalvandoRecorte] = useState(false);
    const [destinatarios, setDestinatarios] = useState(null);
    const [msgConvite, setMsgConvite] = useState('');   // retorno do "Convidar novos elegíveis"
    const [verLista, setVerLista] = useState(false);
    // O card da fila é o <PPIEnvios/> (o mesmo que a Triagem usa). Guardamos aqui a
    // função de recarregar dele, para pedir uma atualização logo depois de publicar
    // — a fila acabou de ganhar as linhas do recorte.
    const enviosRef = useRef(null);
    const opcoes = useOpcoesFiltro(filtros);   // recarrega a cada mudança no recorte

    const carregarDestinatarios = useCallback(async () => {
        try {
            const r = await fetch(`/api/treinamento/ppi/processos/${id}/destinatarios`, { headers: authHeaders() });
            if (r.ok) setDestinatarios(await r.json());
        } catch (_) { /* silencioso */ }
    }, [id]);

    const carregar = useCallback(async () => {
        setCarregando(true); setErro('');
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}`, { headers: authHeaders() });
            if (!resp.ok) throw new Error('Erro ao carregar o processo.');
            const data = await resp.json();
            setProcesso(data.processo); setVagas(data.vagas);
            setFiltros({
                lojas: data.processo.FILTRO_LOJAS || [],
                centrosLucro: data.processo.FILTRO_CENTROS_LUCRO || [],
                cargos: data.processo.FILTRO_CARGOS || [],
            });
        } catch (e) { setErro(e.message); }
        finally { setCarregando(false); }
    }, [id]);

    useEffect(() => { carregar(); carregarDestinatarios(); }, [carregar, carregarDestinatarios]);

    const mudarStatus = async (status) => {
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}`, {
                method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status }),
            });
            if (!resp.ok) throw new Error('Erro ao mudar o status.');
            carregar();
            // Publicar acabou de ENFILEIRAR os destinatários — puxa o placar.
            setTimeout(() => enviosRef.current?.(), 1200);
        } catch (e) { setErro(e.message); }
    };

    const salvarRecorte = async () => {
        setSalvandoRecorte(true); setErro('');
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}`, {
                method: 'PUT', headers: authHeaders(),
                body: JSON.stringify({
                    filtroLojas: filtros.lojas,
                    filtroCentrosLucro: filtros.centrosLucro,
                    filtroCargos: filtros.cargos,
                }),
            });
            if (!resp.ok) throw new Error('Erro ao salvar o público-alvo.');
            await carregarDestinatarios();
        } catch (e) { setErro(e.message); }
        finally { setSalvandoRecorte(false); }
    };

    // Publicar dispara o aviso por WhatsApp — IRREVERSÍVEL e custa dinheiro
    // (template Marketing é cobrado por mensagem). Por isso a confirmação mostra
    // o NÚMERO real de destinatários e o recorte, vindos da prévia.
    const publicar = () => {
        const n = destinatarios ? destinatarios.vaoReceber : '?';
        const recorte = destinatarios ? destinatarios.recorte : '—';
        if (window.confirm(
            `Publicar as inscrições?\n\n` +
            `O aviso será enviado por WhatsApp para ${n} pessoa(s).\n` +
            `Recorte: ${recorte}\n\n` +
            `Esta ação é irreversível.`
        )) {
            mudarStatus('Inscricoes');
        }
    };

    // CONVIDAR OS NOVOS ELEGÍVEIS (2026-08-14) — o botão que resolve "a régua mudou
    // e o processo já está no ar". Manda mensagem PAGA, então segue o mesmo rito do
    // publicar: mostra o número da prévia e pede confirmação.
    //
    // ⚠️ A prévia diz o total de elegíveis de HOJE, não quantos são novos — o número
    // de novos só o servidor sabe (é ele que conhece quem já está na fila). Por isso
    // o confirm fala em "quem ainda não recebeu", sem prometer um número que a tela
    // não tem, e a resposta traz a conta fechada.
    const convidarNovos = async () => {
        const n = destinatarios ? destinatarios.vaoReceber : '?';
        if (!window.confirm(
            `Convidar os novos elegíveis?\n\n`
            + `O recorte de hoje alcança ${n} pessoa(s) — e o WhatsApp vai APENAS para quem ainda não recebeu.\n`
            + `Quem já recebeu ou já se inscreveu não é tocado.\n\n`
            + `Cada mensagem nova é cobrada.`
        )) return;
        setErro(''); setMsgConvite('');
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}/reenfileirar`, {
                method: 'POST', headers: authHeaders(),
            });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Erro ao convidar os novos elegíveis.');
            setMsgConvite(data.message);
            setTimeout(() => enviosRef.current?.(), 1200);   // puxa o placar da fila
            carregarDestinatarios();
        } catch (e) { setErro(e.message); }
    };

    const addVaga = async () => {
        if (!novaVaga.cargo.trim()) return;
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}/vagas`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify(novaVaga),
            });
            if (!resp.ok) throw new Error('Erro ao adicionar a vaga.');
            setNovaVaga(linhaVagaVazia()); carregar();
        } catch (e) { setErro(e.message); }
    };

    const removerVaga = async (idVaga) => {
        if (!window.confirm('Remover esta vaga?')) return;
        try {
            const resp = await fetch(`/api/treinamento/ppi/vagas/${idVaga}`, { method: 'DELETE', headers: authHeaders() });
            const data = await resp.json();
            if (!resp.ok) throw new Error(data.message || 'Erro ao remover a vaga.');
            carregar();
        } catch (e) { setErro(e.message); }
    };

    const apagarProcesso = async () => {
        if (!window.confirm('Apagar TODO o processo (vagas e inscrições)? Esta ação não pode ser desfeita.')) return;
        try {
            const resp = await fetch(`/api/treinamento/ppi/processos/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (!resp.ok) throw new Error('Erro ao apagar o processo.');
            onVoltar();
        } catch (e) { setErro(e.message); }
    };

    const linkPublico = processo ? `${window.location.origin}/ppi-inscricao/${processo.TOKEN_INSCRICAO}` : '';
    const copiarLink = () => {
        navigator.clipboard.writeText(linkPublico);
        setCopiado(true); setTimeout(() => setCopiado(false), 1800);
    };

    if (carregando) return <p style={{ color: CORES.textoFraco }}>Carregando…</p>;
    if (!processo) return <p style={{ color: '#be123c' }}>{erro || 'Processo não encontrado.'}</p>;

    const proxima = proximaEtapa(processo.STATUS);
    const dias = diasAtivo(processo.DATA_PUBLICACAO, processo.DATA_FINALIZACAO);

    // Avanço MANUAL de etapa. Confirmar porque a etapa muda o que o resto do módulo
    // mostra (a Triagem só lista processos que chegaram lá) e Finalizado é o fim da linha.
    const avancar = () => {
        if (!proxima) return;
        const aviso = proxima.chave === 'Finalizado'
            ? '\n\nEsta é a última etapa: o processo é encerrado.'
            : '';
        if (window.confirm(`Avançar o processo para "${proxima.rotulo}"?${aviso}`)) {
            mudarStatus(proxima.chave);
        }
    };

    return (
        <div>
            <button onClick={onVoltar} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                    {processo.CODIGO && <div style={{ ...estiloCodigo, fontSize: '13px' }}>{processo.CODIGO}</div>}
                    <h3 style={{ ...estilos.tituloSecao, margin: '2px 0 0 0' }}>{processo.NOME_PROCESSO}</h3>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Publicar é o único avanço com confirmação especial: dispara WhatsApp
                        (irreversível e cobrado por mensagem). Os demais são só transição. */}
                    {processo.STATUS === 'Rascunho' && <Botao onClick={publicar}>Publicar inscrições</Botao>}
                    {/* Convidar novos elegíveis: só com as inscrições ABERTAS (2026-08-14).
                        Em Rascunho o caminho é publicar; depois de fechadas, o convite
                        levaria a uma página que recusa a pessoa. É `secundario` de
                        propósito — manda WhatsApp pago, mas não é o passo do processo,
                        e competir visualmente com "Avançar" convidaria ao clique errado. */}
                    {processo.STATUS === 'Inscricoes' && (
                        <Botao variant="secundario" icone={UserPlus} onClick={convidarNovos}>
                            Convidar novos elegíveis
                        </Botao>
                    )}
                    {processo.STATUS !== 'Rascunho' && proxima && (
                        <Botao icone={ArrowRight} onClick={avancar}>Avançar → {proxima.rotulo}</Botao>
                    )}
                    <Botao variant="perigo" icone={Trash2} onClick={apagarProcesso}>Apagar</Botao>
                </div>
            </div>

            {/* ── A ESTEIRA + A LINHA DO TEMPO ──
                O RH pediu para ver, sem abrir nada, em que etapa o processo está e
                as datas dos marcos: criado · publicado · quanto tempo ativo · finalizado. */}
            <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', margin: '20px 0' }}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {ETAPAS.map((e, i) => {
                        const atualIdx = ETAPAS.findIndex((x) => x.chave === processo.STATUS);
                        const passou = i < atualIdx;
                        const atual = i === atualIdx;
                        return (
                            <React.Fragment key={e.chave}>
                                {i > 0 && <span style={{ color: passou || atual ? CORES.primaria : CORES.borda, fontWeight: 900 }}>›</span>}
                                <span style={{
                                    padding: '5px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    backgroundColor: atual ? CORES.primaria : (passou ? CORES.primariaBg : CORES.bgSuave),
                                    color: atual ? '#fff' : (passou ? CORES.primariaEscura : CORES.textoFraco),
                                }}>
                                    {e.rotulo}
                                </span>
                            </React.Fragment>
                        );
                    })}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '14px', marginTop: '16px' }}>
                    {/* QUEM entra DENTRO do marco, não como caixa nova (2026-08-14): "quando"
                        e "quem" respondem à mesma pergunta sobre o mesmo ato, e separá-los em
                        duas colunas obrigaria o olho a costurar as duas. Mantém a linha do
                        tempo em 4 colunas — 6 diluiriam os marcos que importam. */}
                    <Marco rotulo="Criado" valor={dataHora(processo.DATA_CRIACAO)}
                        porQuem={processo.NOME_USUARIO_CRIACAO} />
                    <Marco rotulo="Publicado" valor={dataHora(processo.DATA_PUBLICACAO)}
                        porQuem={processo.NOME_USUARIO_PUBLICACAO} />
                    <Marco
                        rotulo={processo.DATA_FINALIZACAO ? 'Esteve ativo' : 'Ativo há'}
                        valor={dias === null ? null : `${dias} dia(s)`}
                    />
                    <Marco rotulo="Finalizado" valor={dataHora(processo.DATA_FINALIZACAO)} />
                </div>
            </div>

            {/* Link público + QR (aparecem a partir de Inscrições abertas) */}
            {['Inscricoes', 'Triagem', 'AplicacaoProva', 'AvaliacaoChao'].includes(processo.STATUS) && (
                <div style={{ backgroundColor: CORES.primariaBg, borderRadius: '12px', padding: '16px', margin: '20px 0', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                    <img
                        src={`/api/treinamento/ppi/public/qr/${processo.TOKEN_INSCRICAO}`}
                        alt="QR code de inscrição"
                        style={{ width: '112px', height: '112px', borderRadius: '8px', backgroundColor: '#fff', padding: '6px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1, minWidth: '240px' }}>
                        <p style={{ fontSize: '12px', fontWeight: 800, color: CORES.primaria, margin: '0 0 4px 0', textTransform: 'uppercase' }}>Link público de inscrição</p>
                        <code style={{ fontSize: '13px', color: CORES.texto, wordBreak: 'break-all' }}>{linkPublico}</code>
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '8px 0 0 0' }}>
                            O QR ao lado leva ao mesmo link — imprima e cole nas lojas. O aviso por WhatsApp (com link + QR) já foi enviado ao publicar.
                        </p>
                    </div>
                    <Botao variant="secundario" icone={copiado ? Check : Copy} onClick={copiarLink}>{copiado ? 'Copiado!' : 'Copiar'}</Botao>
                </div>
            )}

            {erro && <p style={{ color: '#be123c' }}>{erro}</p>}

            {/* O retorno do "Convidar novos elegíveis": quantos entraram AGORA. É a
                única forma de o RH saber se o clique fez algo — sem isso ele clica de
                novo na dúvida, e cada clique é mensagem paga. */}
            {msgConvite && (
                <div style={{ backgroundColor: '#ecfdf5', color: '#047857', padding: '12px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
                    {msgConvite}
                </div>
            )}

            {/* Público-alvo + PRÉVIA dos destinatários (rede de segurança antes do disparo) */}
            <div style={{ border: `1px solid ${CORES.borda}`, borderRadius: '12px', padding: '18px', margin: '24px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                    <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: 0 }}>Público-alvo</h4>
                    <Botao variant="secundario" onClick={salvarRecorte} disabled={salvandoRecorte}>
                        {salvandoRecorte ? 'Salvando…' : 'Salvar público-alvo'}
                    </Botao>
                </div>
                <PublicoAlvo opcoes={opcoes} filtros={filtros} setFiltros={setFiltros} />

                {destinatarios && (
                    <div style={{ marginTop: '16px', backgroundColor: CORES.bgSuave, borderRadius: '10px', padding: '14px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <Send size={16} color={CORES.primaria} />
                            <span style={{ fontSize: '14px', color: CORES.texto }}>
                                O aviso vai para <strong style={{ color: CORES.primaria }}>{destinatarios.vaoReceber}</strong> pessoa(s)
                                {destinatarios.telefoneRuim > 0 && (
                                    <span style={{ color: '#b45309' }}> · {destinatarios.telefoneRuim} com celular inválido ou ausente (não recebem)</span>
                                )}
                            </span>
                            <button onClick={() => setVerLista((v) => !v)} style={btnLink}>
                                {verLista ? <>ocultar lista <ChevronUp size={13} /></> : <>ver lista <ChevronDown size={13} /></>}
                            </button>
                        </div>
                        <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '6px 0 0 26px' }}>
                            Recorte: {destinatarios.recorte}
                        </p>
                        {/* A régua de tempo de cargo já está aplicada no total acima. Sem
                            dizer isso, o número pareceria efeito só do recorte de loja/cargo
                            e o RH concluiria que o filtro cortou gente que ele não cortou. */}
                        {destinatarios.mesesMinimosNoCargo && (
                            <p style={{ fontSize: '12px', color: CORES.textoFraco, margin: '4px 0 0 26px' }}>
                                Já descontados os que não terão <strong>{destinatarios.mesesMinimosNoCargo} meses no cargo</strong>
                                {destinatarios.tempoCargoAte ? ` até o fechamento das inscrições (${dataIsoBR(destinatarios.tempoCargoAte)})` : ''}
                                {/* As três regras que já vêm descontadas do número acima. Dizê-las
                                    aqui é o que evita o RH achar que o recorte de loja/cargo cortou
                                    gente que na verdade foi cortada pelas réguas do programa. */}
                                {destinatarios.diasJanelaSaida
                                    ? ` e os que estão saindo da empresa (aviso prévio, demissão, justa causa ou abandono nos últimos ${destinatarios.diasJanelaSaida} dias)`
                                    : ''}
                                {destinatarios.excecoes > 0
                                    ? `; e já incluídas as ${destinatarios.excecoes} exceção(ões) autorizada(s)`
                                    : ''}.
                            </p>
                        )}
                        {!destinatarios.temRecorte && (
                            <p style={{ fontSize: '12px', color: '#b45309', margin: '8px 0 0 26px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <AlertTriangle size={13} /> Sem recorte de loja/cargo: o aviso vai para <strong>todos os ativos que cumprem o tempo de cargo</strong>.
                            </p>
                        )}

                        {verLista && (
                            <div style={{ ...estilos.tabelaWrap, marginTop: '12px', maxHeight: '260px', overflowY: 'auto' }}>
                                <table style={estilos.tabela}>
                                    <thead><tr>
                                        <th style={estilos.th}>Nome</th>
                                        <th style={estilos.th}>Loja</th>
                                        <th style={estilos.th}>Centro de lucro</th>
                                        <th style={estilos.th}>Cargo</th>
                                        <th style={estilos.th}>Celular</th>
                                        <th style={estilos.th}>WhatsApp</th>
                                    </tr></thead>
                                    <tbody>
                                        {destinatarios.lista.map((d, i) => (
                                            <tr key={i}>
                                                <td style={estilos.td}>{d.nome}</td>
                                                <td style={estilos.td}>{d.loja || '—'}</td>
                                                <td style={estilos.td}>{d.centroLucro || '—'}</td>
                                                <td style={estilos.td}>{d.cargo || '—'}</td>
                                                <td style={{ ...estilos.td, fontVariantNumeric: 'tabular-nums' }}>{d.celular || '—'}</td>
                                                <td style={estilos.td}>
                                                    {/* O MOTIVO é o que diz ao RH o que corrigir no cadastro —
                                                        "não recebe" sozinho não ajuda ninguém. */}
                                                    {d.recebe
                                                        ? <BadgeStatus tom="aprovado">recebe</BadgeStatus>
                                                        : <BadgeStatus tom="alerta" title={d.motivo}>{d.motivo || 'não recebe'}</BadgeStatus>}
                                                </td>
                                            </tr>
                                        ))}
                                        {!destinatarios.lista.length && (
                                            <tr><td style={estilos.td} colSpan={6}>Nenhum funcionário no recorte.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* EXCEÇÕES — logo depois do Público-alvo, de propósito: as duas respondem
                "quem participa". O recorte diz a regra; a exceção diz quem foi
                dispensado dela. Autorizar aqui muda a prévia acima, por isso o
                `onMudou` a recarrega. */}
            <PPIExcecoes idProcesso={id} onMudou={carregarDestinatarios} />

            {/* ENVIOS — a fila do aviso de inscrições (mesmo card usado na Triagem). */}
            <PPIEnvios idProcesso={id} grupo="inscricoes" titulo="Envios do aviso" recarregarRef={enviosRef} />

            {/* Vagas */}
            <h4 style={{ fontSize: '16px', fontWeight: 800, color: CORES.texto, margin: '24px 0 12px 0' }}>Vagas</h4>
            <div style={estilos.tabelaWrap}>
                <table style={estilos.tabela}>
                    <thead><tr>
                        <th style={estilos.th}>Cargo</th>
                        <th style={estilos.th}>Data</th>
                        <th style={estilos.th}>Hora</th>
                        <th style={estilos.th}>Local</th>
                        <th style={estilos.th}>Inscritos</th>
                        <th style={{ ...estilos.th, textAlign: 'center' }}></th>
                    </tr></thead>
                    <tbody>
                        {vagas.map((v) => (
                            <tr key={v.ID}>
                                <td style={estilos.td}><strong>{v.CARGO}</strong></td>
                                <td style={estilos.td}>{v.DATA_AVALIACAO ? new Date(v.DATA_AVALIACAO).toLocaleDateString('pt-BR') : '—'}</td>
                                <td style={estilos.td}>{v.HORA_AVALIACAO ? String(v.HORA_AVALIACAO).slice(0, 5) : '—'}</td>
                                <td style={estilos.td}>{v.LOCAL_AVALIACAO || '—'}</td>
                                <td style={estilos.td}>{v.QTD_CANDIDATOS}</td>
                                <td style={{ ...estilos.td, textAlign: 'center' }}>
                                    <button onClick={() => removerVaga(v.ID)} style={btnIconePerigo} title="Remover vaga"><Trash2 size={15} /></button>
                                </td>
                            </tr>
                        ))}
                        {!vagas.length && <tr><td style={estilos.td} colSpan={6}>Nenhuma vaga cadastrada.</td></tr>}
                    </tbody>
                </table>
            </div>

            {/* Adicionar vaga */}
            <div style={{ ...linhaVaga, marginTop: '16px' }}>
                <select style={{ ...estilos.input, flex: 2 }} value={novaVaga.cargo} onChange={(e) => setNovaVaga((v) => ({ ...v, cargo: e.target.value }))} title="Cargo da vaga">
                    <option value="">Cargo…</option>
                    {CARGOS_VAGA.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <input type="date" style={{ ...estilos.input, flex: 1 }} value={novaVaga.dataAvaliacao} onChange={(e) => setNovaVaga((v) => ({ ...v, dataAvaliacao: e.target.value }))} />
                <input type="time" style={{ ...estilos.input, width: '110px' }} value={novaVaga.horaAvaliacao} onChange={(e) => setNovaVaga((v) => ({ ...v, horaAvaliacao: e.target.value }))} />
                <CampoLocal valor={novaVaga.localAvaliacao} onChange={(l) => setNovaVaga((v) => ({ ...v, localAvaliacao: l }))} />
                <Botao variant="secundario" icone={Plus} onClick={addVaga}>Adicionar</Botao>
            </div>
        </div>
    );
}

// ─── Auxiliares de layout ────────────────────────────────────────────────────
const Campo = ({ label, children }) => (
    <div><label style={estilos.rotuloFiltro}>{label}</label>{children}</div>
);

// Um marco da linha do tempo. Vazio mostra "—" (e não some): a ausência da data é
// informação — diz que o processo ainda não chegou lá.
//
// `porQuem` (2026-08-14) é o autor do ato, quando conhecido. ⚠️ A linha do autor só
// aparece se o marco JÁ ACONTECEU (`valor` preenchido): um "por —" embaixo de um
// "Publicado —" seria ruído duplo dizendo a mesma coisa. E a ausência do nome num
// marco que aconteceu é FIEL, não bug: os processos publicados antes desta data não
// têm autor gravado e não há de onde tirá-lo — preencher com o criador seria
// fabricar auditoria (ver a migration-2026-08-14-ppi-usuario-publicacao.sql).
const Marco = ({ rotulo, valor, porQuem }) => (
    <div>
        <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', color: CORES.textoFraco, letterSpacing: '0.3px' }}>{rotulo}</div>
        <div style={{
            fontSize: '14px', fontWeight: 700, marginTop: '4px',
            color: valor ? CORES.texto : '#94a3b8', fontVariantNumeric: 'tabular-nums',
        }}>
            {valor || '—'}
        </div>
        {valor && porQuem && (
            <div style={{ fontSize: '12px', color: CORES.textoFraco, marginTop: '3px', lineHeight: 1.35 }}>
                por {porQuem}
            </div>
        )}
    </div>
);

// O código do processo (PPI-2026-001): monoespaçado para o RH conferir dígito a
// dígito, e discreto — ele identifica, o título é que descreve.
const estiloCodigo = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    fontSize: '12px', fontWeight: 800, color: CORES.primaria, letterSpacing: '0.5px',
};

// (O placar da fila migrou para o PPIEnvios — é o mesmo card na Triagem.)
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' };
const linhaVaga = { display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap' };
const btnVoltar = { display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: 'none', color: CORES.textoSec, cursor: 'pointer', fontSize: '14px', fontWeight: 600, marginBottom: '16px', padding: 0 };
const btnIconePerigo = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '8px', borderRadius: '8px', border: '1px solid #fbb6c4', backgroundColor: '#fff', color: '#e11d48', cursor: 'pointer' };
const btnLink = { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: CORES.primaria, cursor: 'pointer', fontSize: '13px', fontWeight: 700, padding: 0 };

export default PPIProcessos;
